<template>
  <div>
    <div v-if="history && history.length > 0">
      <div class="table-responsive">
        <table class="table table-centered table-bordered table-nowrap mb-0 table-hover">
          <thead class="thead-light">
            <tr>
              <th style="width: 150px">{{ $t("audit-control.history.date_user") }}</th>
              <th>{{ $t("audit-control.history.action.text") }}</th>
              <th>{{ $t("audit-finding.status.text") }}</th>
              <th>{{ $t("common.note") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in paginatedHistory" :key="item.date">
              <td>
                <small class="d-block text-muted">{{ item.date | date }}</small>
                <span class="font-weight-bold">{{ item.performedBy.name }} {{ item.performedBy.surname }}</span>
              </td>
              <td>
                <b-badge :variant="getActionVariant(item.action)">{{ $t(`audit-control.history.action.${item.action}`) }}</b-badge>
                <div v-if="item.description" class="small text-muted mt-1">{{ item.description }}</div>

                <!-- Display Changes -->
                <div v-if="item.changes" class="mt-2">
                  <ul class="list-unstyled mb-0 sm">
                    <li v-for="(change, key) in item.changes" :key="key" class="font-size-11">
                      <span class="font-weight-bold">{{ $t(`audit-finding.${key}`) }}:</span>
                      <span class="text-danger strike-through">{{ formatValue(change.old, key) }}</span>
                      <span class="mx-1 text-muted"><i class="iconoir-arrow-right"></i></span>
                      <span class="text-success">{{ formatValue(change.new, key) }}</span>
                    </li>
                  </ul>
                </div>
              </td>

              <!-- Snapshot Fields -->
              <td :class="{ 'bg-soft-warning': isChanged(item, 'status') }">
                <b-badge v-if="getSnapshotValue(item, 'status')" :variant="getStatusVariant(getSnapshotValue(item, 'status'))">
                  {{ $t(`audit-finding.status.${getSnapshotValue(item, "status").toLowerCase()}`) }}
                </b-badge>
                <span v-else>-</span>
              </td>

              <td :class="{ 'bg-soft-warning': isChanged(item, 'closureNote') }">
                <span v-b-tooltip.hover :title="getLocalizedValue(getSnapshotValue(item, 'closureNote'))" class="d-inline-block text-truncate" style="max-width: 150px;">
                  {{ getLocalizedValue(getSnapshotValue(item, "closureNote")) || "-" }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="row mt-4">
        <div class="col">
          <b-pagination v-model="currentPage" :total-rows="history.length" :per-page="perPage" limit="10" size="sm"></b-pagination>
        </div>
      </div>
    </div>
    <empty v-else>
      <h5 class="text-center">{{ $t("common.no-data") }}</h5>
    </empty>
  </div>
</template>

<script>
export default {
  props: {
    history: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
    };
  },
  computed: {
    paginatedHistory() {
      // Sort desc by date
      const sorted = [...this.history].sort((a, b) => new Date(b.date) - new Date(a.date));
      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;
      return sorted.slice(start, end);
    },
    currentLanguage() {
      return this.$i18n.locale;
    },
  },
  methods: {
    getSnapshotValue(item, field) {
      if (!item.snapshot) return null;
      return item.snapshot[field];
    },
    isChanged(item, field) {
      return item.changes && item.changes[field] !== undefined;
    },
    getLocalizedValue(val) {
      if (!val) return "";
      if (typeof val !== "object") return val;
      return val[this.$i18n.locale] || val.en || val.tr || "";
    },
    formatValue(val, key) {
      if (val === null || val === undefined) return "-";
      
      if (typeof val === "string") {
        const isEnum = /^[A-Z0-9_]+$/.test(val);
        if (isEnum) {
          const enumKey = val.toLowerCase().replace(/_/g, '-');
          if (key === "status") return this.$te(`audit-finding.status.${enumKey}`) ? this.$t(`audit-finding.status.${enumKey}`) : val;
          if (key === "action") return this.$te(`audit-control.history.action.${val}`) ? this.$t(`audit-control.history.action.${val}`) : val;
        }
        return val;
      }

      if (Array.isArray(val)) {
        if (val.length === 0) return "[]";
        return val.map(v => {
          if (typeof v === 'object' && v !== null) {
            if (v.name) return this.getLocalizedValue(v.name);
            if (v.no) return v.no;
            if (v.tr || v.en || v[this.currentLanguage]) return v[this.currentLanguage] || v.tr || v.en;
            if (v._id) return v._id;
          }
          return v;
        }).join(", ");
      }

      if (typeof val === "object" && val !== null) {
        if (val.tr || val.en) {
          return val[this.currentLanguage] || val.tr || val.en;
        }
        if (val instanceof Date || (typeof val === "string" && val.match(/^\d{4}-\d{2}-\d{2}/))) {
          return new Date(val).toLocaleDateString();
        }
        return JSON.stringify(val);
      }
      return val;
    },
    getActionVariant(action) {
      switch (action) {
        case "CREATED":
          return "success";
        case "UPDATED":
          return "info";
        case "APPROVED":
          return "success";
        case "REJECTED":
          return "danger";
        case "REQUEST_APPROVAL":
          return "warning";
        case "REQUEST_EDIT":
          return "info";
        default:
          return "primary";
      }
    },
    getStatusVariant(status) {
      switch (status) {
        case "AWAITING_APPROVAL":
          return "danger";
        case "ACTION_PLAN_MONITORING":
          return "primary";
        case "EXTENSION_APPROVAL":
          return "warning";
        case "COMPLETED":
          return "success";
        case "PASSIVE":
          return "secondary";
        case "CANCELED":
          return "danger";
        default:
          return "warning";
      }
    },
  },
};
</script>
